import {
  GiftItemStatus,
  IGiftItemResponse,
  IOrganisationResponse,
  ITicketPrice,
} from '@one-tree/library'
import isEqual from 'react-fast-compare'
import {
  bulkReorderGiftItemQuestions,
  patchGiftItem,
} from '../../../../../helpers/APIItemHelper'
import { BulkQuestionReorder, IGiftItemPostBody } from '../../../../../types/GiftItemTypes'

export function getPriceChanges(
  newPrices?: ITicketPrice[],
  oldPrices?: ITicketPrice[],
): ITicketPrice[] | false {
  if (!newPrices) return false

  if (oldPrices) {
    return newPrices.filter(
      (newPrice) => !oldPrices.some((oldPrice) => isEqual(newPrice, oldPrice)),
    )
  }
  return newPrices
}

export async function updatePriceQuestions(
  organisation: IOrganisationResponse,
  prices: ITicketPrice[] | false,
): Promise<boolean> {
  if (!prices) return false

  const patchEachPrice = prices.map((price) => patchGiftItem({
      format: organisation.format,
      itemId: price.id,
      patchOptions: { enabledQuestionIds: price.enabledQuestionIds },
    }))

  const pricesRes = await Promise.all([...patchEachPrice])

  return pricesRes.every((response) => Boolean(response))
}

export function normalizeReorders(
  reorders: BulkQuestionReorder[],
  ticket: IGiftItemResponse,
  changes: IGiftItemPostBody | null,
): BulkQuestionReorder[] {
  const effectivePrices = changes?.prices ?? ticket.prices ?? []
  const effectiveTicketIds = changes?.enabledQuestionIds ?? ticket.enabledQuestionIds ?? []

  return reorders
    .map((reorder) => {
      const price = effectivePrices.find(({ id }) => id === reorder.itemId)
      const activeIds = new Set(
        price ? (price.enabledQuestionIds ?? []) : effectiveTicketIds,
      )

      const questions = reorder.questions
        .filter(({ id }) => activeIds.has(id))
        .map((q, i) => ({ ...q, order: i + 1 }))

      return { ...reorder, questions }
    })
    .filter(({ questions }) => questions.length > 0)
}

export async function saveQuestionOrder(
  organisation: IOrganisationResponse,
  order: BulkQuestionReorder[],
): Promise<boolean> {
  if (!order.length) return false

  const responses = await Promise.all(
    order.map(({ itemId, questions }) => bulkReorderGiftItemQuestions({
      format: organisation.format,
      itemId,
      questions,
    })),
  )

  return responses.every((response) => Boolean(response))
}

export function publishFreeTicketWarning(ticket: IGiftItemResponse): boolean {
  if (ticket.status === GiftItemStatus.Unpublished) {
    if (ticket.prices && ticket.prices.length > 0) {
      const hasFreePrice = ticket.prices.some(({ value }) => value === '0')
      return hasFreePrice
    }

    return ticket.value === '0'
  }
  return false
}

export function priceUsingQuestion(
  questionId: number,
  prices?: ITicketPrice[],
): boolean {
  if (!prices) return false

  return prices?.some((price) => {
    const notArchived = price.status !== GiftItemStatus.Archived
    const hasQuestion = price.enabledQuestionIds?.includes(questionId)

    return notArchived && hasQuestion
  })
}
